import React from 'react';
import { List, Show, useListContext, useShowContext, useRefresh, useDataProvider, useGetList } from 'react-admin';
import { useBreadcrumbContext } from '../components/layout/AppBreadcrumb';
import { Smartphone, Trash2, CheckCircle, XCircle, Shield, Activity, AlertTriangle } from 'lucide-react';
import { SearchIntegration } from '../components/common/SearchIntegration';
import { ReactAdminPagination } from '../components/ui/react-admin-pagination';
import { ListScreen } from '../components/ui/list-screen';
import { StatCard, StatRow } from '../components/ui/stat-card';
import { SegmentedTabs } from '../components/ui/segmented-tabs';
import { DataTable, DataColumn } from '../components/ui/data-table';
import { SortMenu } from '../components/ui/sort-menu';
import { FilterTokens } from '../components/ui/filter-controls';
import { ConfirmDialog } from '../components/ui/confirm-dialog';
import { AvatarName, StatusBadge, RowActions } from '../components/ui/data-cells';
import { toastSuccess, toastError } from '../lib/toast';
import { cn } from '../lib/utils';
import { CustomDataProvider } from '../types/dataProvider';

/* ============================================================
   Device tokens — Latio design system (derived, log-table treatment).
   ============================================================ */

interface DeviceToken {
  id: number;
  user_name?: string;
  user_email?: string;
  platform?: string;
  token?: string;
  is_active?: boolean;
  last_used?: string;
  created_at?: string;
  device_id?: string;
  app_version?: string;
  os_version?: string;
  notification_count?: number;
}

const daysSince = (v?: string) => (v ? Math.floor((Date.now() - new Date(v).getTime()) / 86400000) : null);
const platformLabel = (p?: string) => (p ? p.charAt(0).toUpperCase() + p.slice(1) : 'Unknown');

type Health = { label: string; cls: string; Icon: any; note: string };
function health(r: DeviceToken): Health {
  if (!r.is_active) return { label: 'Inactive', cls: 'bg-error-bg text-error', Icon: XCircle, note: 'Deactivated — will not receive notifications.' };
  const d = daysSince(r.last_used);
  if (d === null || d <= 7) return { label: 'Healthy', cls: 'bg-success-bg text-success', Icon: CheckCircle, note: 'Actively receiving notifications.' };
  if (d <= 30) return { label: 'Stale', cls: 'bg-warning-bg text-warning', Icon: AlertTriangle, note: "Hasn't been used recently — verify if still active." };
  return { label: 'Dead', cls: 'bg-error-bg text-error', Icon: XCircle, note: 'Unused for over 30 days — consider deactivating.' };
}

const fmtDate = (v?: string | null) =>
  v ? new Date(v).toLocaleDateString('en-US', { month: 'short', day: '2-digit', year: 'numeric' }) : '—';
const fmtDateTime = (v?: string | null) =>
  v ? new Date(v).toLocaleString('en-US', { month: 'short', day: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

/* ---- List ---- */
const TOKEN_TABS = [
  { value: 'all', label: 'All' },
  { value: 'android', label: 'Android' },
  { value: 'ios', label: 'iOS' },
];
const TOKEN_SORT = [
  { field: 'created_at', label: 'Registered' },
  { field: 'last_used', label: 'Last used' },
  { field: 'platform', label: 'Platform' },
  { field: 'id', label: 'ID' },
];
const TOKEN_SORT_LABELS = Object.fromEntries(TOKEN_SORT.map((f) => [f.field, f.label]));

const TokenPlatformTabs = () => {
  const { filterValues, setFilters, displayedFilters } = useListContext();
  const current = (filterValues.platform as string) || 'all';
  return (
    <SegmentedTabs
      tabs={TOKEN_TABS}
      value={current}
      onChange={(v) => setFilters({ ...filterValues, platform: v === 'all' ? undefined : v }, displayedFilters)}
    />
  );
};

const TokenStats = () => {
  const base = { pagination: { page: 1, perPage: 1 }, sort: { field: 'created_at', order: 'DESC' } } as const;
  const all = useGetList('device-tokens', { ...base, filter: {} });
  const active = useGetList('device-tokens', { ...base, filter: { active_only: true } });
  const android = useGetList('device-tokens', { ...base, filter: { platform: 'android' } });
  const ios = useGetList('device-tokens', { ...base, filter: { platform: 'ios' } });
  const n = (v?: number) => (v === undefined ? '—' : v);
  return (
    <StatRow>
      <StatCard primary label="Total tokens" value={n(all.total)} />
      <StatCard label="Active" value={n(active.total)} />
      <StatCard label="Android" value={n(android.total)} />
      <StatCard label="iOS" value={n(ios.total)} />
    </StatRow>
  );
};

function HealthChip({ token }: { token: DeviceToken }) {
  const h = health(token);
  return (
    <span className={cn('inline-flex items-center gap-1 rounded-pill px-2 py-0.5 text-[11px] font-semibold', h.cls)}>
      <h.Icon className="size-3" />
      {h.label}
    </span>
  );
}

function DeactivateAction({ token }: { token: DeviceToken }) {
  const [deleteable, setOpen] = React.useState(false);
  const dataProvider = useDataProvider<CustomDataProvider>();
  const refresh = useRefresh();
  if (!token.is_active) return <span className="text-[13px] text-ink-3">—</span>;
  const deactivate = async () => {
    try {
      await dataProvider.delete('device-tokens', { id: token.id, previousData: token });
      toastSuccess('Device token deactivated');
      refresh();
    } catch {
      toastError('Failed to deactivate token');
    }
  };
  return (
    <RowActions>
      <button
        type="button"
        onClick={() => setOpen(true)}
        title="Deactivate token"
        className="inline-flex size-8 items-center justify-center rounded-pill text-ink-2 hover:bg-error-bg hover:text-error"
      >
        <Trash2 className="size-4" />
      </button>
      <ConfirmDialog
        open={deleteable}
        onOpenChange={setOpen}
        tone="destructive"
        title="Deactivate device token?"
        consequence="The user will no longer receive notifications on this device. This cannot be undone."
        confirmLabel="Deactivate"
        onConfirm={deactivate}
      />
    </RowActions>
  );
}

const TokensListBody = () => {
  const columns: DataColumn<DeviceToken>[] = [
    { key: 'id', header: 'ID', sortable: true, width: '1px', cell: (r) => <span className="tabular-nums text-ink-2">#{r.id}</span> },
    {
      key: 'user',
      header: 'User',
      sortable: true,
      sortField: 'user_name',
      cell: (r) => <AvatarName name={r.user_name || 'Unknown'} subtext={r.user_email} size={28} />,
    },
    { key: 'platform', header: 'Platform', sortable: true, cell: (r) => <span className="text-ink-2">{platformLabel(r.platform)}</span> },
    {
      key: 'token',
      header: 'Token',
      cell: (r) => <span className="font-mono text-[12px] text-ink-2">{r.token ? `${r.token.slice(0, 18)}…` : '—'}</span>,
    },
    { key: 'is_active', header: 'Status', sortable: true, cell: (r) => <StatusBadge status={r.is_active ? 'active' : 'suspended'} /> },
    { key: 'health', header: 'Health', cell: (r) => <HealthChip token={r} /> },
    {
      key: 'last_used',
      header: 'Last used',
      sortable: true,
      cell: (r) => {
        const d = daysSince(r.last_used);
        return r.last_used ? <span className="text-ink-2">{fmtDate(r.last_used)}{d !== null && ` · ${d}d`}</span> : <span className="text-ink-3">Never</span>;
      },
    },
    { key: 'created_at', header: 'Registered', sortable: true, cell: (r) => fmtDate(r.created_at) },
    { key: 'actions', header: '', cell: (r) => <DeactivateAction token={r} /> },
  ];
  return (
    <ListScreen
      stats={<TokenStats />}
      tabs={<TokenPlatformTabs />}
      actions={<SortMenu fields={TOKEN_SORT} />}
      tokens={<FilterTokens fields={[]} sortLabels={TOKEN_SORT_LABELS} />}
    >
      <DataTable columns={columns} rowClick="show" emptyMessage="No device tokens found." />
      <div className="pt-1">
        <ReactAdminPagination />
      </div>
    </ListScreen>
  );
};

export const DeviceTokensList = (props: any) => (
  <List {...props} actions={false} empty={false} pagination={false} sort={{ field: 'created_at', order: 'DESC' }} perPage={50} component="div">
    <SearchIntegration filterKey="q" />
    <TokensListBody />
  </List>
);

/* ---- Detail ---- */
function DetailCard({ title, icon: Icon, children, className }: { title?: string; icon?: any; children: React.ReactNode; className?: string }) {
  return (
    <div className={cn('rounded-card border border-hairline bg-background p-5', className)}>
      {title && (
        <h3 className="mb-3 flex items-center gap-2 text-[15px] font-bold text-ink">
          {Icon && <Icon className="size-4 text-ink-2" />}
          {title}
        </h3>
      )}
      {children}
    </div>
  );
}
function Fact({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-3 py-1.5">
      <span className="text-[13px] text-ink-2">{label}</span>
      <span className="text-right text-sm font-medium text-ink">{value}</span>
    </div>
  );
}

const DeviceTokenShowContent = () => {
  const { record, isLoading } = useShowContext<DeviceToken>();
  const dataProvider = useDataProvider<CustomDataProvider>();
  const refresh = useRefresh();
  const { setData } = useBreadcrumbContext();
  const [open, setOpen] = React.useState(false);

  React.useEffect(() => {
    if (record) setData({ resourceId: record.id.toString(), resourceName: `Token #${record.id}`, resourceType: 'session' });
  }, [record, setData]);

  if (isLoading) return <div className="p-6 text-center text-ink-2">Loading token…</div>;
  if (!record) return <div className="p-6 text-center text-ink-2">Token not found</div>;

  const h = health(record);
  const d = daysSince(record.last_used);

  const deactivate = async () => {
    try {
      await dataProvider.delete('device-tokens', { id: record.id, previousData: record });
      toastSuccess('Device token deactivated');
      refresh();
    } catch {
      toastError('Failed to deactivate token');
    }
  };

  return (
    <div className="space-y-6 p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-center gap-4">
          <span className="flex size-[60px] items-center justify-center rounded-card bg-indigo-tint text-indigo">
            <Smartphone className="size-7" />
          </span>
          <div>
            <div className="flex items-center gap-3">
              <h1 className="text-[24px] font-bold leading-none text-ink" style={{ letterSpacing: '-0.02em' }}>
                Device token #{record.id}
              </h1>
              <StatusBadge status={record.is_active ? 'active' : 'suspended'} />
            </div>
            <p className="mt-1 text-sm text-ink-2">{platformLabel(record.platform)} · {record.user_name || 'Unknown'}</p>
          </div>
        </div>
        {record.is_active && (
          <button
            onClick={() => setOpen(true)}
            className="inline-flex h-9 items-center gap-2 rounded-pill bg-error px-4 text-sm font-semibold text-white hover:bg-error/90"
          >
            <Trash2 className="size-4" />
            Deactivate
          </button>
        )}
      </div>

      <div className={cn('flex items-start gap-2 rounded-card border px-4 py-3 text-sm', h.cls, 'border-current/20')}>
        <h.Icon className="mt-0.5 size-4 shrink-0" />
        <span><strong>{h.label} token.</strong> {h.note}</span>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[1.6fr_1fr]">
        <div className="space-y-6">
          <DetailCard title="Device" icon={Smartphone}>
            <div className="divide-y divide-hairline">
              <Fact label="Platform" value={platformLabel(record.platform)} />
              <Fact label="Device ID" value={record.device_id || '—'} />
              <Fact label="App version" value={record.app_version || '—'} />
              <Fact label="OS version" value={record.os_version || '—'} />
            </div>
          </DetailCard>

          <DetailCard title="Token" icon={Shield}>
            <p className="break-all rounded-input bg-surface-2 px-3 py-2 font-mono text-[12px] text-ink">{record.token || 'No token available'}</p>
          </DetailCard>
        </div>

        <div className="space-y-6">
          <DetailCard title="User">
            <div className="divide-y divide-hairline">
              <Fact label="Name" value={record.user_name || 'Unknown'} />
              <Fact label="Email" value={record.user_email || '—'} />
            </div>
          </DetailCard>

          <DetailCard title="Activity" icon={Activity}>
            <div className="divide-y divide-hairline">
              <Fact label="Registered" value={fmtDateTime(record.created_at)} />
              <Fact label="Last used" value={record.last_used ? `${fmtDateTime(record.last_used)}${d !== null ? ` · ${d}d ago` : ''}` : 'Never'} />
              <Fact label="Notifications" value={record.notification_count ?? '—'} />
            </div>
          </DetailCard>
        </div>
      </div>

      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        tone="destructive"
        title="Deactivate device token?"
        consequence="The user will no longer receive notifications on this device. This cannot be undone."
        confirmLabel="Deactivate"
        onConfirm={deactivate}
      />
    </div>
  );
};

export const DeviceTokensShow = (props: any) => (
  <Show {...props} actions={false} title=" " component="div">
    <DeviceTokenShowContent />
  </Show>
);
